import React, { useState, useEffect } from 'react';
import { Dialog, Box, Typography, IconButton, Switch, Checkbox } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { axiosPrivate } from '../../../axios/axios';
import { masterAPI } from '../dataConfig';
import { color } from '../../../StyleData/Config';
import TableContainer from '../../../Component/TableContainer';
import ModelMasterModal from './ModelMasterModal';

const ModelMaster = () => {
  const actionsAllow = useSelector(state => state.dataRed.actionsAllow);
  const navigate = useNavigate();

  const [models, setModels] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('');
  const [filterValue, setFilterValue] = useState("enableData");
  const [backUpData, setBackUpData] = useState([]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const link = masterAPI["master_model"].link;
      const res = await axiosPrivate.get(link);
      const data = res.data.result || res.data || [];

      // Sort data by model name or code
      data.sort((a, b) => {
        return a.code?.localeCompare(b.code, undefined, {
          numeric: true,
          sensitivity: 'base'
        }) || 0;
      });

      if (filterValue === "enableData") {
        const filterDataResult = data.filter(fil => fil.isActive);
        setModels(filterDataResult);
        setBackUpData(data);
      } else {
        setModels(data);
        setBackUpData(data);
      }
    } catch (err) {
      console.error('Error fetching models:', err);
      setModels([]);
      setBackUpData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (data) => {
    setEditData(data);
    setMode('Edit');
    setModalOpen(true);
  };

  const handleDelete = async (e, id) => {
    const enableLink = masterAPI["master_model"].enable;
    const disableLink = masterAPI["master_model"].disable;

    try {
      if (e.target.checked) {
        await axiosPrivate.put(`${enableLink}/${id}`);
      } else {
        await axiosPrivate.put(`${disableLink}/${id}`);
      }
      fetchModels();
    } catch (error) {
      console.error('Error updating model status:', error);
    }
  };

  const handleChangeFilter = (e) => {
    if (e === "showAll") {
      setModels(backUpData);
    } else {
      const filterDataResult = backUpData.filter(fil => fil.isActive);
      setModels(filterDataResult);
    }
    setFilterValue(e);
  };

  const handleViewAttributes = (modelData) => {
    navigate('/masters/model-spec-attributes', {
      state: { modelData }
    });
  };


  const columns = [
    {
      accessor: "id",
      disableFilters: true,
      Cell: () => {
        return (
          <Box sx={{ width: color.checkBoxWidth }}>
            <Checkbox />
          </Box>
        );
      },
    },
    {
      Header: 'Model Name',
      accessor: 'modelName',
      Cell: ({ cell }) => {
        return (
          <Typography
            sx={{
              fontSize: "inherit",
              fontWeight: "inherit",
              color: "#0d6efd",
              textDecoration: "underline",
              cursor: "pointer"
            }}
            onClick={() => handleViewAttributes(cell.row.original)}
          >
            {cell.value}
          </Typography>
        );
      },
    },
    { Header: 'Model Code', accessor: 'code' },
    { Header: 'Technical Spec Name', accessor: 'technicalSpecificationName' },
    {
      Header: 'Actions',
      Cell: ({ cell }) => {
        return (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <IconButton disabled={!actionsAllow.edit}>
              <EditOutlined
                onClick={() => {
                  handleEdit(cell.row.original);
                }}
                sx={{
                  fontSize: color.masterEditDelButton,
                  color: actionsAllow.edit ? color.masterEditDelButtonColor : "default",
                }}
              />
            </IconButton>
            <Switch
              disabled={!actionsAllow.delete}
              checked={cell.row.original.isActive}
              onChange={(e) => { handleDelete(e, cell.row.original.id) }}
            />
          </Box>
        );
      },
    }
  ];

  const handleButtonNew = () => {
    setMode('Create');
    setEditData(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditData(null);
    setMode('');
    fetchModels();
  };

  return (
    <>
      <Box>
        <Typography
          sx={{
            color: color.masterPageTitleColor,
            fontSize: color.masterPageTitleFontSize,
            fontWeight: color.masterPageTitleFontWeight,
            mb: color.masterPageTitlemd,
          }}
        >
          {masterAPI["master_model"].title}
        </Typography>
      </Box>

      <TableContainer
        columns={columns}
        data={models}
        loading={loading}
        buttonNew={handleButtonNew}
        buttonDisable={!actionsAllow.create}
        filterValue={filterValue}
        handleChange={handleChangeFilter}
        filterShow={true}
      />

      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth={'md'}
        fullWidth
      >
        <ModelMasterModal
          mode={mode}
          handleCloseClick={handleModalClose}
          editData={editData}
          onRefresh={fetchModels}
        />
      </Dialog>
    </>
  );
};

export default ModelMaster;
